import { computed, ref, type ComputedRef } from 'vue';
import { boundsFromCorners, type NxPlanBounds, type NxPlanPoint } from './geometry';
import type { NxPlanMarqueeMode } from './hitTest';

export interface NxPlanMarqueeOptions {
  /**
   * How the mode follows the drag direction. `direction` (default): left to
   * right selects what lies completely inside (`contain`), right to left what
   * touches it (`intersect`) — the CAD convention. Or a fixed mode.
   */
  mode?: NxPlanMarqueeMode | 'direction';
}

export interface NxPlanMarqueeState {
  /** Drag start and current corner in world units; `null` while idle. */
  from: ComputedRef<NxPlanPoint | null>;
  to: ComputedRef<NxPlanPoint | null>;
  active: ComputedRef<boolean>;
  /** The spanned rectangle while active. */
  bounds: ComputedRef<NxPlanBounds | null>;
  mode: ComputedRef<NxPlanMarqueeMode>;
  begin: (point: NxPlanPoint) => void;
  update: (point: NxPlanPoint) => void;
  /** Ends the drag and returns the final rectangle and mode (`null` if idle). */
  end: () => { bounds: NxPlanBounds; mode: NxPlanMarqueeMode } | null;
  cancel: () => void;
}

/**
 * State of a marquee (rubber-band) selection in world units, fed from the
 * world pointer events of `NxPlanCanvas`. Draw it with `NxPlanMarquee`, select
 * with `selectInBounds(items, bounds, mode, shapeOf)`.
 */
export function useNxPlanMarquee(options: NxPlanMarqueeOptions = {}): NxPlanMarqueeState {
  const from = ref<NxPlanPoint | null>(null);
  const to = ref<NxPlanPoint | null>(null);
  const active = computed(() => from.value !== null && to.value !== null);
  const bounds = computed(() =>
    from.value && to.value ? boundsFromCorners(from.value, to.value) : null
  );
  const mode = computed<NxPlanMarqueeMode>(() => {
    const fixed = options.mode ?? 'direction';
    if (fixed !== 'direction') return fixed;
    return from.value && to.value && to.value.x < from.value.x ? 'intersect' : 'contain';
  });
  return {
    from: computed(() => from.value),
    to: computed(() => to.value),
    active,
    bounds,
    mode,
    begin(point) {
      from.value = { ...point };
      to.value = { ...point };
    },
    update(point) {
      if (from.value) to.value = { ...point };
    },
    end() {
      const result = bounds.value ? { bounds: bounds.value, mode: mode.value } : null;
      from.value = null;
      to.value = null;
      return result;
    },
    cancel() {
      from.value = null;
      to.value = null;
    },
  };
}
